/**
 * main.ts — DOM entry point.
 *
 * Slim wrapper around createPlotexEngine(). Handles:
 *   - DOM element lookups (canvas, loading overlay, HUD stats)
 *   - Loading overlay show/hide
 *   - FPS counter display in the HUD
 *   - window.__plotex dev-console exposure
 *
 * All engine internals (renderer, scene, camera, loaders, LOD manager,
 * post-processing, animate loop) live in index.ts via the factory.
 */

import { createPlotexEngine } from './index';

async function main() {
  const canvas = document.getElementById('canvas') as HTMLCanvasElement;
  const loadingOverlay = document.getElementById('loading')!;
  const loadingText = document.getElementById('loading-text')!;
  const statsEl = document.getElementById('stats')!;

  loadingText.textContent = 'Loading scene\u2026';

  // Drive the loading overlay's percentage label from the engine's
  // onProgress callback. ratio is [0, 1]; when lengthComputable is
  // false (rare — most CDNs send Content-Length for static files),
  // ratio stays at 0 and we keep the plain "Loading scene…" string
  // so users don't see a stuck "0%".
  let engine;
  try {
    engine = await createPlotexEngine({
      canvas,
      onProgress: (ratio: number) => {
        if (ratio <= 0) {
          loadingText.textContent = 'Loading scene\u2026';
          return;
        }
        const pct = Math.max(1, Math.min(100, Math.round(ratio * 100)));
        loadingText.textContent = `Loading scene\u2026 ${pct}%`;
      },
    });
  } catch (err) {
    console.error('[plotex] Engine init failed:', err);
    loadingText.textContent = `Error: ${(err as Error).message}`;
    return;
  }

  // ---- Hide loading overlay ----------------------------------------------
  loadingOverlay.classList.add('hidden');
  (loadingOverlay as HTMLElement).style.display = 'none';

  // ---- Expose for devtools / automated inspection ------------------------
  // Forward the engine handle as-is so every field added by
  // createPlotexEngine (impostorPairs, liveSync, future additions) shows
  // up under `window.__plotex` without needing a manual whitelist.
  (window as any).__plotex = engine;

  // ---- HUD FPS counter ---------------------------------------------------
  let frameCount = 0;
  let lastFpsSample = performance.now();
  function updateHud() {
    requestAnimationFrame(updateHud);
    frameCount++;
    const now = performance.now();
    if (now - lastFpsSample >= 500) {
      const fps = (frameCount * 1000) / (now - lastFpsSample);
      frameCount = 0;
      lastFpsSample = now;
      statsEl.innerHTML = `
        <div>Meshes: <strong>${engine!.scene.children.length}</strong></div>
        <div>FPS: <strong>${fps.toFixed(0)}</strong></div>
      `;
    }
  }
  updateHud();
}

main().catch((err) => {
  console.error(err);
  const t = document.getElementById('loading-text');
  if (t) t.textContent = `Error: ${err.message}`;
});
